import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Select } from '../components/Select'
import { Input } from '../components/Input'
import { FormNavigation } from '../components/FormNavigation'
import { useOnboardingStore } from '../store/onboardingStore'
import { useTenant } from '../context/TenantContext'
import { useTestMode } from '../context/TestModeContext'
import { documentService } from '../services/api'
import { reportUploadError } from '../services/errorReporter'
import { MAX_UPLOAD_BYTES, formatFileSize } from '../utils/fileLimits'
import { toast } from 'sonner'
import { Upload, X, FileText } from 'lucide-react'
import { logger } from '../utils/logger'
import { buildInsuranceCountries, getInsurers, INSURANCE_COUNTRY_ISO } from '../data/insurers'
import { useIsItaly } from './italy/useIsItaly'
import { ItalyGuardianStep } from './italy/ItalyGuardianStep'
import type { StepProps, InsuranceType } from '../types'

export function Step4Insurance(props: StepProps) {
  // Italy replaces this whole step with "Tutor legale" — no insurance section
  // (change request 2026-08-25, §4). Same step slot, so no step-count changes.
  const isItaly = useIsItaly()
  if (isItaly) return <ItalyGuardianStep {...props} />
  return <InsuranceStep {...props} />
}

// PVS GuardianRelationshipType values offered in the DE/ES dropdown; the
// stored value IS the enum, labels come from i18n (step4.guardianTypes.*).
const GUARDIAN_TYPE_OPTIONS = [
  'parent',
  'legal_guardian_minor',
  'support_administrator',
  'authorized_representative',
  'youth_welfare_office',
  'foster_parent',
  'supplementary_curator',
  'other',
] as const

function InsuranceStep({ onNext, onBack }: StepProps) {
  const { t, i18n } = useTranslation()
  const insuranceCountries = buildInsuranceCountries(i18n.language, t('step4.otherCountry'))
  const { tenant } = useTenant()
  const isTestMode = useTestMode()
  const { insuranceData, setInsuranceData, guardianData, setGuardianData, personalData, addressData, patientNumber } = useOnboardingStore()
  const [errors, setErrors] = useState<{ insuranceProvider?: string; insuranceProviderOther?: string; guardianType?: string; guardianName?: string; guardianAddress?: string; guardianScope?: string }>({})
  const [isUploading, setIsUploading] = useState(false)

  // Spanish org offers a "no insurance" option, preselected by default
  const isSpain = tenant?.country === 'ES'
  const noInsurance = insuranceData.insuranceType === 'none'

  // Initialize insurance country from tenant default or address country
  useEffect(() => {
    if (!insuranceData.insuranceCountry || insuranceData.insuranceCountry === 'Germany') {
      const defaultCountry = tenant?.defaultCountry || addressData.country || 'Germany'
      const validCountry = INSURANCE_COUNTRY_ISO[defaultCountry] ? defaultCountry : 'Other'
      if (validCountry !== insuranceData.insuranceCountry) {
        setInsuranceData({ insuranceCountry: validCountry })
      }
    }
  }, [tenant?.defaultCountry, addressData.country]) // eslint-disable-line react-hooks/exhaustive-deps

  // Spanish org: preselect "no insurance" when nothing chosen yet
  useEffect(() => {
    if (isSpain && !insuranceData.insuranceType) {
      setInsuranceData({ insuranceType: 'none' })
    }
  }, [isSpain]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset provider when country or type changes
  useEffect(() => {
    if (insuranceData.insuranceProvider && insuranceData.insuranceProvider !== 'Other') {
      const availableInsurers = getInsurers(insuranceData.insuranceCountry, insuranceData.insuranceType)
      const isProviderValid = availableInsurers.some(i => i.value === insuranceData.insuranceProvider)
      if (!isProviderValid) {
        setInsuranceData({ insuranceProvider: '', insuranceProviderOther: '' })
      }
    }
  }, [insuranceData.insuranceCountry, insuranceData.insuranceType, insuranceData.insuranceProvider, setInsuranceData])

  const handleTypeChange = (type: InsuranceType) => {
    setInsuranceData({ insuranceType: type, insuranceProvider: '', insuranceProviderOther: '' })
    setErrors({})
  }

  const handleCountryChange = (country: string) => {
    setInsuranceData({ insuranceCountry: country, insuranceProvider: '', insuranceProviderOther: '' })
    setErrors({})
  }

  const handleProviderChange = (provider: string) => {
    setInsuranceData({ insuranceProvider: provider })
    if (provider !== 'Other') {
      setInsuranceData({ insuranceProviderOther: '' })
    }
    if (errors.insuranceProvider) {
      setErrors(prev => ({ ...prev, insuranceProvider: undefined }))
    }
  }

  const handleOtherChange = (value: string) => {
    setInsuranceData({ insuranceProviderOther: value })
    if (errors.insuranceProviderOther) {
      setErrors(prev => ({ ...prev, insuranceProviderOther: undefined }))
    }
  }

  const availableInsurers = getInsurers(insuranceData.insuranceCountry, insuranceData.insuranceType)
    .map(insurer => {
      if (insurer.value === '') return { ...insurer, label: t('common.pleaseSelect') }
      if (insurer.value === 'Other') return { ...insurer, label: t('step4.otherInsurance') }
      return insurer
    })
  const showOtherInput = insuranceData.insuranceProvider === 'Other'

  // Mirror structured guardian fields into the legacy combined columns so
  // webhooks, Slack notes and old consumers keep working unchanged.
  const setGuardianNames = (patch: { guardianFirstName?: string; guardianLastName?: string }) => {
    const first = patch.guardianFirstName ?? guardianData.guardianFirstName ?? ''
    const last = patch.guardianLastName ?? guardianData.guardianLastName ?? ''
    setGuardianData({ ...patch, guardianName: [first, last].filter(Boolean).join(' ') })
    if (errors.guardianName) setErrors(prev => ({ ...prev, guardianName: undefined }))
  }
  const setGuardianAddressParts = (patch: Partial<Pick<typeof guardianData, 'guardianStreet' | 'guardianHouseNumber' | 'guardianPostalCode' | 'guardianCity'>>) => {
    const street = patch.guardianStreet ?? guardianData.guardianStreet ?? ''
    const nr = patch.guardianHouseNumber ?? guardianData.guardianHouseNumber ?? ''
    const plz = patch.guardianPostalCode ?? guardianData.guardianPostalCode ?? ''
    const city = patch.guardianCity ?? guardianData.guardianCity ?? ''
    const joined = [[street, nr].filter(Boolean).join(' '), [plz, city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    setGuardianData({ ...patch, guardianAddress: joined })
    if (errors.guardianAddress) setErrors(prev => ({ ...prev, guardianAddress: undefined }))
  }

  // Guardian validation - if hasGuardian is true, the structured fields are required
  const guardianFieldsValid = !guardianData.hasGuardian || (
    !!guardianData.guardianType &&
    (guardianData.guardianFirstName || '').trim().length >= 1 &&
    (guardianData.guardianLastName || '').trim().length >= 1 &&
    (guardianData.guardianStreet || '').trim().length >= 2 &&
    (guardianData.guardianPostalCode || '').trim().length >= 2 &&
    (guardianData.guardianCity || '').trim().length >= 2 &&
    guardianData.guardianScope.trim().length >= 2
  )

  const insuranceValid =
    noInsurance ||
    (!!insuranceData.insuranceType &&
      !!insuranceData.insuranceCountry &&
      !!insuranceData.insuranceProvider &&
      (insuranceData.insuranceProvider !== 'Other' || (insuranceData.insuranceProviderOther?.trim() || '').length >= 2))

  const isValid = insuranceValid && guardianFieldsValid

  // Handle guardian document upload
  const handleGuardianDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      toast.error(t('common.onlyPdfAllowed'))
      return
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(t('common.fileTooLarge', { size: formatFileSize(file.size), max: formatFileSize(MAX_UPLOAD_BYTES) }))
      return
    }

    // Test mode never writes to the document buckets — mark the document as
    // present locally so the flow continues, but skip the actual upload.
    if (isTestMode) {
      toast.success(t('common.testModeUploadSkipped'))
      setGuardianData({ guardianDocumentFilename: `TESTMODE_${file.name}` })
      return
    }

    setIsUploading(true)
    try {
      const response = await documentService.uploadPdf(file, patientNumber, 'guardian')
      if (response.success && response.data) {
        setGuardianData({ guardianDocumentFilename: response.data.filename })
      }
    } catch (error) {
      logger.error('Upload failed:', error)
      reportUploadError(error, { patientNumber, step: 'step4', filename: file.name })
      toast.error(t('common.uploadFailed'))
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveGuardianDocument = () => {
    setGuardianData({ guardianDocumentFilename: '' })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const newErrors: typeof errors = {}

    if (!noInsurance) {
      if (!insuranceData.insuranceProvider) {
        newErrors.insuranceProvider = t('step4.selectInsurance')
      }

      if (insuranceData.insuranceProvider === 'Other' && !(insuranceData.insuranceProviderOther?.trim())) {
        newErrors.insuranceProviderOther = t('step4.enterInsuranceName')
      }
    }

    // Guardian validation
    if (guardianData.hasGuardian) {
      if (!guardianData.guardianType) {
        newErrors.guardianType = t('step4.guardianTypeRequired')
      }
      if (!(guardianData.guardianFirstName || '').trim() || !(guardianData.guardianLastName || '').trim()) {
        newErrors.guardianName = t('step4.guardianNameRequired')
      }
      if (!(guardianData.guardianStreet || '').trim() || !(guardianData.guardianPostalCode || '').trim() || !(guardianData.guardianCity || '').trim()) {
        newErrors.guardianAddress = t('step4.guardianAddressRequired')
      }
      if (!guardianData.guardianScope.trim()) {
        newErrors.guardianScope = t('step4.guardianScopeRequired')
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    onNext()
  }

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="form-inner py-12">
        <h2 className="text-h2 text-foreground mb-2">
          {t('step4.title')}
        </h2>
        <p className="text-body text-foreground mb-8">
          {personalData.firstName} {personalData.lastName}
        </p>

        <div className="space-y-6">
          {/* Insurance Type Selection */}
          <div>
            <label className="label-text">{t('step4.insuranceType')} *</label>
            <div className={`grid ${isSpain ? 'grid-cols-3' : 'grid-cols-2'} gap-4 mt-2`}>
              <button
                type="button"
                onClick={() => handleTypeChange('public')}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  insuranceData.insuranceType === 'public'
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      insuranceData.insuranceType === 'public' ? 'border-primary' : 'border-gray-300'
                    }`}
                  >
                    {insuranceData.insuranceType === 'public' && (
                      <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                    )}
                  </div>
                  <span className="font-medium">{t('step4.public')}</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleTypeChange('private')}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  insuranceData.insuranceType === 'private'
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      insuranceData.insuranceType === 'private' ? 'border-primary' : 'border-gray-300'
                    }`}
                  >
                    {insuranceData.insuranceType === 'private' && (
                      <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                    )}
                  </div>
                  <span className="font-medium">{t('step4.private')}</span>
                </div>
              </button>

              {/* "No insurance" option — Spanish org only */}
              {isSpain && (
                <button
                  type="button"
                  onClick={() => handleTypeChange('none')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    noInsurance
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        noInsurance ? 'border-primary' : 'border-gray-300'
                      }`}
                    >
                      {noInsurance && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                    </div>
                    <span className="font-medium">{t('step4.noInsurance')}</span>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Country Selection - only show after type is selected (not for "no insurance") */}
          {insuranceData.insuranceType && !noInsurance && (
            <Select
              id="insuranceCountry"
              label={`${t('step4.insuranceCountry')} *`}
              value={insuranceData.insuranceCountry}
              onChange={(e) => handleCountryChange(e.target.value)}
              options={insuranceCountries}
            />
          )}

          {/* Insurance Provider Selection - only show after country is selected (not for "no insurance") */}
          {insuranceData.insuranceType && insuranceData.insuranceCountry && !noInsurance && (
            <Select
              id="insuranceProvider"
              label={`${t('step4.insuranceProvider')} *`}
              value={insuranceData.insuranceProvider}
              onChange={(e) => handleProviderChange(e.target.value)}
              options={availableInsurers}
              error={errors.insuranceProvider}
            />
          )}

          {/* Other Insurance Input - only show when "Other" is selected */}
          {showOtherInput && (
            <Input
              id="insuranceProviderOther"
              type="text"
              label={`${t('step4.otherProvider')} *`}
              placeholder={t('step4.otherProviderPlaceholder')}
              value={insuranceData.insuranceProviderOther || ''}
              onChange={(e) => handleOtherChange(e.target.value)}
              error={errors.insuranceProviderOther}
            />
          )}
        </div>

        {/* Guardian Section */}
        <div className="space-y-6 mt-8">
          <div>
            <label className="label-text">{t('step4.guardianLabel')}</label>
            <div className="grid grid-cols-2 gap-4 mt-2">
            <button
              type="button"
              onClick={() => setGuardianData({ hasGuardian: false })}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                !guardianData.hasGuardian
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    !guardianData.hasGuardian ? 'border-primary' : 'border-gray-300'
                  }`}
                >
                  {!guardianData.hasGuardian && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  )}
                </div>
                <span className="font-medium">{t('common.no')}</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setGuardianData({ hasGuardian: true })}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                guardianData.hasGuardian
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    guardianData.hasGuardian ? 'border-primary' : 'border-gray-300'
                  }`}
                >
                  {guardianData.hasGuardian && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  )}
                </div>
                <span className="font-medium">{t('common.yes')}</span>
              </div>
            </button>
            </div>
          </div>

          {/* Guardian Details - only show when hasGuardian is true */}
          {guardianData.hasGuardian && (
            <div className="space-y-4">
              <div>
                <label htmlFor="guardianType" className="label-text">{t('step4.guardianTypeLabel')} *</label>
                <select
                  id="guardianType"
                  value={guardianData.guardianType || ''}
                  onChange={(e) => {
                    setGuardianData({ guardianType: e.target.value })
                    if (errors.guardianType) setErrors(prev => ({ ...prev, guardianType: undefined }))
                  }}
                  className="w-full px-4 py-3 text-body border border-border rounded-input outline-none bg-white focus:border-primary mt-2"
                >
                  <option value="">{t('common.pleaseSelect')}</option>
                  {GUARDIAN_TYPE_OPTIONS.map((v) => (
                    <option key={v} value={v}>{t(`step4.guardianTypes.${v}`)}</option>
                  ))}
                </select>
                {errors.guardianType && (
                  <p className="text-small text-destructive mt-1">{errors.guardianType}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  id="guardianFirstName"
                  type="text"
                  label={`${t('step2.firstName')} *`}
                  value={guardianData.guardianFirstName || ''}
                  onChange={(e) => setGuardianNames({ guardianFirstName: e.target.value })}
                  error={errors.guardianName}
                />
                <Input
                  id="guardianLastName"
                  type="text"
                  label={`${t('step2.lastName')} *`}
                  value={guardianData.guardianLastName || ''}
                  onChange={(e) => setGuardianNames({ guardianLastName: e.target.value })}
                />
              </div>

              <div>
                <label className="label-text">{t('step4.guardianAddressLabel')} *</label>
                <div className="grid grid-cols-3 gap-4 mt-2">
                  <div className="col-span-2">
                    <Input
                      id="guardianStreet"
                      type="text"
                      label={t('step4.guardianStreetLabel')}
                      value={guardianData.guardianStreet || ''}
                      onChange={(e) => setGuardianAddressParts({ guardianStreet: e.target.value })}
                      error={errors.guardianAddress}
                    />
                  </div>
                  <Input
                    id="guardianHouseNumber"
                    type="text"
                    label={t('step4.guardianHouseNumberLabel')}
                    value={guardianData.guardianHouseNumber || ''}
                    onChange={(e) => setGuardianAddressParts({ guardianHouseNumber: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <Input
                    id="guardianPostalCode"
                    type="text"
                    label={t('step4.guardianPostalCodeLabel')}
                    value={guardianData.guardianPostalCode || ''}
                    onChange={(e) => setGuardianAddressParts({ guardianPostalCode: e.target.value })}
                  />
                  <div className="col-span-2">
                    <Input
                      id="guardianCity"
                      type="text"
                      label={t('step4.guardianCityLabel')}
                      value={guardianData.guardianCity || ''}
                      onChange={(e) => setGuardianAddressParts({ guardianCity: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  id="guardianPhone"
                  type="tel"
                  label={t('step2.phone')}
                  value={guardianData.guardianPhone || ''}
                  onChange={(e) => setGuardianData({ guardianPhone: e.target.value })}
                />
                <Input
                  id="guardianEmail"
                  type="email"
                  label={t('step2.email')}
                  value={guardianData.guardianEmail || ''}
                  onChange={(e) => setGuardianData({ guardianEmail: e.target.value })}
                />
              </div>

              <Input
                id="guardianScope"
                type="text"
                label={`${t('step4.guardianScopeLabel')} *`}
                placeholder={t('step4.guardianScopePlaceholder')}
                value={guardianData.guardianScope}
                onChange={(e) => {
                  setGuardianData({ guardianScope: e.target.value })
                  if (errors.guardianScope) setErrors(prev => ({ ...prev, guardianScope: undefined }))
                }}
                error={errors.guardianScope}
              />

              {/* Document Upload */}
              <div>
                <label className="label-text">{t('step4.guardianDocumentLabel')}</label>
                <p className="text-small text-muted-foreground mb-2">
                  {t('step4.guardianDocumentHint')}
                </p>

                {guardianData.guardianDocumentFilename ? (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <FileText className="w-5 h-5 text-green-600" />
                    <span className="flex-1 text-small text-green-700 truncate">
                      {guardianData.guardianDocumentFilename}
                    </span>
                    <button
                      type="button"
                      onClick={handleRemoveGuardianDocument}
                      className="p-1 hover:bg-green-100 rounded"
                    >
                      <X className="w-4 h-4 text-green-600" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                    <Upload className="w-5 h-5 text-muted-foreground" />
                    <span className="text-small text-muted-foreground">
                      {isUploading ? t('common.uploadingFile') : t('common.uploadPdf')}
                    </span>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                      onChange={handleGuardianDocumentUpload}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </label>
                )}
              </div>
            </div>
          )}
        </div>

        <FormNavigation onBack={onBack} nextDisabled={!isValid || isUploading} />
      </form>
    </div>
  )
}
